import type { ReactNode } from 'react';
import type { RequestStatus, Role } from '@geoic/shared';
import { cn } from '@/lib/cn.js';

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-n-100 text-n-800',
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-error-50 text-error-600',
  info: 'bg-info-50 text-info-600',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Ariza holatlari (D2: Yangi · Ishda · Taklif yuborildi · Yutildi · Yo'qotildi · Spam). */
const REQUEST_STATUS: Record<RequestStatus, { label: string; tone: BadgeTone }> = {
  new: { label: 'Yangi', tone: 'info' },
  in_progress: { label: 'Ishda', tone: 'brand' },
  quoted: { label: 'Taklif yuborildi', tone: 'warning' },
  won: { label: 'Yutildi', tone: 'success' },
  lost: { label: 'Yo‘qotildi', tone: 'neutral' },
  spam: { label: 'Spam', tone: 'danger' },
};

export function RequestStatusBadge({ status }: { status: RequestStatus }) {
  const config = REQUEST_STATUS[status];
  return <Badge tone={config.tone}>{config.label}</Badge>;
}

/** Rol nomlari — TZ 3.2 dagi atamalar bilan. */
export const ROLE_LABELS: Record<Role, string> = {
  superadmin: 'Bosh administrator',
  admin: 'Administrator',
  editor: 'Muharrir',
  sales: 'Savdo menejeri',
};

export function RoleBadge({ role }: { role: Role }) {
  const tone: BadgeTone =
    role === 'superadmin' ? 'brand' : role === 'sales' ? 'warning' : 'neutral';
  return <Badge tone={tone}>{ROLE_LABELS[role]}</Badge>;
}

/** Nashr holati — kontent ro'yxatlarida. */
export function PublishBadge({ isPublished }: { isPublished: boolean }) {
  return (
    <Badge tone={isPublished ? 'success' : 'neutral'}>
      {isPublished ? 'Nashr qilingan' : 'Qoralama'}
    </Badge>
  );
}
